import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
//utilities
const searchInTree = (subModules, key) => subModules.some((sub) => sub.key === key || (sub.sub_modules?.length ? searchInTree(sub.sub_modules, key) : false));
const findTopLevelParent = (modules, childKey) => modules.find((m) => m.key === childKey || searchInTree(m.sub_modules ?? [], childKey)) ?? null;
const findSiblings = (subModules, targetKey) => {
    for (const sub of subModules) {
        if (sub.key === targetKey)
            return subModules;
        if (sub.sub_modules?.length) {
            const found = findSiblings(sub.sub_modules, targetKey);
            if (found)
                return found;
        }
    }
    return null;
};
const replaceSubModule = (subModules, targetKey, newData) => subModules.map((sub) => {
    if (sub.key === targetKey) {
        return {
            _id: sub._id,
            order: newData.order ?? sub.order,
            key: newData.key ?? sub.key,
            module_name: newData.module_name ?? sub.module_name,
            url: newData.url ?? sub.url,
            permissions: newData.permissions ?? sub.permissions,
            sub_modules: newData.sub_modules ?? sub.sub_modules ?? [],
        };
    }
    return sub.sub_modules?.length
        ? { ...sub, sub_modules: replaceSubModule(sub.sub_modules, targetKey, newData) }
        : sub;
});
const removeSubModuleByKey = (subModules, targetKey) => subModules
    .filter((sub) => sub.key !== targetKey)
    .map((sub) => sub.sub_modules?.length
    ? { ...sub, sub_modules: removeSubModuleByKey(sub.sub_modules, targetKey) }
    : sub);
const isDuplicateOrder = (modules, order, excludeKey) => modules.some((m) => m.order === order && m.key !== excludeKey);
const initialState = {
    modules: [],
    loading: false,
    error: null,
    saving: false,
    saveError: null,
    deleting: false,
    deleteError: null,
};
export const fetchModules = createAsyncThunk("modules/fetchAll", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const res = await ApiCall("GET", "/api/modules", undefined, {
            Authorization: `Bearer ${token}`,
        });
        if (res && "data" in res && res.data?.success)
            return res.data.data || [];
        return rejectWithValue(res?.data?.message || "Failed to fetch modules");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch modules error");
    }
});
export const createModule = createAsyncThunk("modules/create", async ({ payload }, { getState, rejectWithValue, dispatch }) => {
    try {
        const token = getState().auth.token;
        const allModules = getState().modules.modules;
        if (isDuplicateOrder(allModules, payload.order)) {
            toast.warning("Duplicate order not allowed");
            return rejectWithValue("Duplicate order not allowed");
        }
        const res = await ApiCall("POST", "/api/modules/add-modules", payload, {
            Authorization: `Bearer ${token}`,
        });
        if (res && "data" in res && res.data?.success) {
            toast.success("Module created successfully");
            return await dispatch(fetchModules()).unwrap();
        }
        const msg = res?.data?.message || "Failed to create module";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Create module error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
export const updateModule = createAsyncThunk("modules/update", async ({ editingModule, payload }, { getState, rejectWithValue, dispatch }) => {
    try {
        const token = getState().auth.token;
        const allModules = getState().modules.modules;
        const isTopLevel = allModules.some((m) => m._id === editingModule._id);
        if (isTopLevel) {
            if (isDuplicateOrder(allModules, payload.order, editingModule.key)) {
                toast.warning("Duplicate order not allowed");
                return rejectWithValue("Duplicate order not allowed");
            }
            const res = await ApiCall("PUT", `/api/modules/${editingModule._id}`, payload, { Authorization: `Bearer ${token}`, "x-module-key": editingModule.key });
            if (res && "data" in res && res.data?.success) {
                toast.success("Module updated successfully");
                return await dispatch(fetchModules()).unwrap();
            }
            const msg = res?.data?.message || "Failed to update module";
            toast.warning(msg);
            return rejectWithValue(msg);
        }
        // Sub-module update
        const topLevelParent = findTopLevelParent(allModules, editingModule.key);
        if (!topLevelParent)
            return rejectWithValue("Top-level parent not found");
        const siblings = findSiblings(topLevelParent.sub_modules ?? [], editingModule.key);
        if (siblings && isDuplicateOrder(siblings, payload.order, editingModule.key)) {
            toast.warning("Duplicate order not allowed");
            return rejectWithValue("Duplicate order not allowed");
        }
        const updatedSubModules = replaceSubModule(topLevelParent.sub_modules ?? [], editingModule.key, payload);
        const res = await ApiCall("PUT", `/api/modules/${topLevelParent._id}`, {
            order: topLevelParent.order,
            key: topLevelParent.key,
            module_name: topLevelParent.module_name,
            sub_modules: updatedSubModules,
        }, { Authorization: `Bearer ${token}`, "x-module-key": editingModule.key });
        if (res && "data" in res && res.data?.success) {
            toast.success("Module updated successfully");
            return await dispatch(fetchModules()).unwrap();
        }
        const msg = res?.data?.message || "Failed to update module";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Update module error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
export const deleteModule = createAsyncThunk("modules/delete", async ({ module }, { getState, rejectWithValue, dispatch }) => {
    try {
        const token = getState().auth.token;
        const allModules = getState().modules.modules;
        const isTopLevel = allModules.some((m) => m._id === module._id);
        if (isTopLevel) {
            const res = await ApiCall("DELETE", `/api/modules/${module._id}`, null, { Authorization: `Bearer ${token}`, "x-module-key": module.key });
            if (res && "data" in res && res.data?.success) {
                toast.success("Module deleted successfully");
                return await dispatch(fetchModules()).unwrap();
            }
            const msg = res?.data?.message || "Failed to delete module";
            toast.warning(msg);
            return rejectWithValue(msg);
        }
        // Sub-module deletion
        const topLevelParent = findTopLevelParent(allModules, module.key);
        if (!topLevelParent)
            return rejectWithValue("Top-level parent not found");
        const updatedSubModules = removeSubModuleByKey(topLevelParent.sub_modules ?? [], module.key);
        const updatePayload = {
            order: topLevelParent.order,
            key: topLevelParent.key,
            module_name: topLevelParent.module_name,
            sub_modules: updatedSubModules,
        };
        if (!topLevelParent.is_parent) {
            updatePayload.url = topLevelParent.url || "";
            updatePayload.permissions = topLevelParent.permissions;
        }
        const res = await ApiCall("PUT", `/api/modules/${topLevelParent._id}`, updatePayload, { Authorization: `Bearer ${token}`, "x-module-key": module.key });
        if (res && "data" in res && res.data?.success) {
            toast.success("Module deleted successfully");
            return await dispatch(fetchModules()).unwrap();
        }
        const msg = res?.data?.message || "Failed to delete module";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Delete module error";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
// slice
const moduleSlice = createSlice({
    name: "modules",
    initialState,
    reducers: {
        clearModuleErrors: (state) => {
            state.error = null;
            state.saveError = null;
            state.deleteError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchModules.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchModules.fulfilled, (state, action) => { state.loading = false; state.modules = action.payload; })
            .addCase(fetchModules.rejected, (state, action) => { state.loading = false; state.error = action.payload || "Failed to fetch modules"; })
            .addCase(createModule.pending, (state) => { state.saving = true; state.saveError = null; })
            .addCase(createModule.fulfilled, (state, action) => { state.saving = false; state.modules = action.payload; })
            .addCase(createModule.rejected, (state, action) => { state.saving = false; state.saveError = action.payload || "Failed to create module"; })
            .addCase(updateModule.pending, (state) => { state.saving = true; state.saveError = null; })
            .addCase(updateModule.fulfilled, (state, action) => { state.saving = false; state.modules = action.payload; })
            .addCase(updateModule.rejected, (state, action) => { state.saving = false; state.saveError = action.payload || "Failed to update module"; })
            .addCase(deleteModule.pending, (state) => { state.deleting = true; state.deleteError = null; })
            .addCase(deleteModule.fulfilled, (state, action) => { state.deleting = false; state.modules = action.payload; })
            .addCase(deleteModule.rejected, (state, action) => { state.deleting = false; state.deleteError = action.payload || "Failed to delete module"; });
    },
});
export const { clearModuleErrors } = moduleSlice.actions;
// selectors
export const selectAllModules = (state) => state.modules.modules;
export const selectModulesLoading = (state) => state.modules.loading;
export const selectModulesError = (state) => state.modules.error;
export const selectModulesSaving = (state) => state.modules.saving;
export const selectModulesDeleting = (state) => state.modules.deleting;
export default moduleSlice.reducer;
